/**
 * Touch and mouse on the planet: drag to turn (with momentum after letting go), tap to walk there,
 * pinch or scroll to zoom out, turn two fingers to turn the view.
 */
import type { Loop } from '@core/loop';
import type { Game } from '@game/game';
import { canWalk, setViewTarget } from '@game/view/panels';
import type { World } from '@world/index';

import type { Walker } from './walk';

const TAP_SLOP = 7;

export function attachPointer(
  canvas: HTMLCanvasElement,
  game: Game,
  world: World,
  walker: Walker,
  loop: Loop,
): () => void {
  const local = game.local;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pointers = new Map<number, { x: number; y: number }>();
  let drag: { x: number; y: number; x0: number; y0: number; t: number; moved: boolean } | null =
    null;
  /** Two fingers: their distance and the zoom when they landed, and the angle between them last seen. */
  let pinch: { d: number; v: number; a: number } | null = null;

  const onDown = (e: PointerEvent): void => {
    if (!canWalk(game)) return;
    canvas.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) {
      drag = {
        x: e.clientX,
        y: e.clientY,
        x0: e.clientX,
        y0: e.clientY,
        t: performance.now(),
        moved: false,
      };
      local.vel.x = 0;
      local.vel.y = 0;
      local.finger.x = 0;
      local.finger.y = 0;
      local.walk = null;
      local.dragging = true;
    } else {
      drag = null;
      local.dragging = false;
      const [a, b] = [...pointers.values()];
      if (a && b)
        pinch = {
          d: Math.hypot(a.x - b.x, a.y - b.y),
          v: local.viewTarget,
          a: Math.atan2(b.y - a.y, b.x - a.x),
        };
    }
    loop.wake();
  };

  const onMove = (e: PointerEvent): void => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch && pointers.size >= 2) {
      const [a, b] = [...pointers.values()];
      if (a && b) {
        setViewTarget(game, pinch.v - (Math.hypot(a.x - b.x, a.y - b.y) - pinch.d) / 300);
        // turning the fingers turns the view around me
        const angle = Math.atan2(b.y - a.y, b.x - a.x);
        let delta = angle - pinch.a;
        if (delta > Math.PI) delta -= 2 * Math.PI;
        else if (delta < -Math.PI) delta += 2 * Math.PI;
        pinch.a = angle;
        walker.turnView(delta);
      }
      loop.wake();
      return;
    }
    if (!drag) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (!drag.moved && Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) < TAP_SLOP) return;
    drag.moved = true;
    drag.x = e.clientX;
    drag.y = e.clientY;
    const now = performance.now();
    const dt = Math.max(1, now - drag.t);
    drag.t = now;
    // the finger's speed; the walker's heavy planet chases it (game/spin.ts)
    local.finger.x = (dx / dt) * 16;
    local.finger.y = (dy / dt) * 16;
    local.finger.at = now;
    if (reduced) walker.turn(dx, dy);
    loop.wake(100);
  };

  const tap = (x: number, y: number): void => {
    const tile = world.pickTile(x, y);
    if (tile >= 0) walker.walkToTile(tile, performance.now());
  };

  const onUp = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = null;
    if (drag && !drag.moved && e.type === 'pointerup') tap(e.clientX, e.clientY);
    if (drag?.moved && reduced) {
      local.vel.x = 0;
      local.vel.y = 0;
      local.finger.x = 0;
      local.finger.y = 0;
    }
    drag = null;
    local.dragging = false;
    loop.wake(600);
  };

  const onWheel = (e: WheelEvent): void => {
    if (!canWalk(game)) return;
    e.preventDefault();
    setViewTarget(game, local.viewTarget + e.deltaY / 600);
    loop.wake();
  };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onUp);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  return () => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onUp);
    canvas.removeEventListener('wheel', onWheel);
  };
}
